import { useEffect, useState } from "react"
import { array, object, func, string } from 'prop-types';

import { useForm, useFormErrors, useManagedContext } from "../../hooks"
import { FormError, InputLista, Modal, TableCustom, FormLabel } from "../../common"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import { ServerRequest } from "../../../utils/apiweb";
import { REQUEST_METHOD } from '@laramie/ui';
import { APIS } from "../../../config/apis";

import { onRequestError, onRequestNoSuccess } from "../../../utils/requests";
import { Tab, Tabs } from '../../common/TabCustom';
import {TIPS} from "../../../consts/tips";
import { getColumnasEquivalentes } from './utils';

// Una definición con firmas de antes de las columnas, y sin columnas, las sigue editando en su
// recuadro y se imprime con su formato hasta que alguien la pase a dos columnas.
const tieneFirmasAnteriores = (convenio) => Boolean(convenio.firmas) && !(convenio.firmasIzquierda || convenio.firmasDerecha)

const ConvenioDefinicionesModal = (props) => {
    
    const entityInit = {
        id: 0,
        codigo: '',
        nombre: '',
        idTipoTributo: 0,
        convenio: '',
        condiciones: '',
        firmas: '',
        firmasIzquierda: '',
        firmasDerecha: ''
    }

    const [entity, setEntity] = useState(entityInit)

    const [tabActive, setTabActive] = useState("condiciones");
    // Campo donde se inserta el parámetro elegido: el último de la pestaña que tuvo el foco.
    const [campoActivo, setCampoActivo] = useState("condiciones");
    const [firmasEnColumnas, setFirmasEnColumnas] = useState(true);
    
    const { setIsLoading } = useManagedContext()

    const [formValues, formHandle, _, formSet] = useForm({
        codigo: '',
        nombre: '',
        idTipoTributo: 0,
        convenio: '',
        condiciones: '',
        firmas: '',
        firmasIzquierda: '',
        firmasDerecha: ''
    })

    useEffect(() => {
        setEntity(props.convenio)
        setFirmasEnColumnas(!tieneFirmasAnteriores(props.convenio))
        formSet({
            codigo: props.convenio.codigo,
            nombre: props.convenio.nombre,
            idTipoTributo: props.convenio.idTipoTributo,
            convenio: props.convenio.convenio,
            condiciones: props.convenio.condiciones,
            firmas: props.convenio.firmas ?? '',
            firmasIzquierda: props.convenio.firmasIzquierda ?? '',
            firmasDerecha: props.convenio.firmasDerecha ?? ''
        })
    }, [props.convenio])

    const onTabSelected = (tab) => {
        setTabActive(tab)
        setCampoActivo(tab !== 'firmas' ? tab : firmasEnColumnas ? 'firmasIzquierda' : 'firmas')
    }

    // Pasar a dos columnas es una acción explícita: las columnas se precargan con el equivalente de
    // las firmas del recuadro, y al guardar, el texto anterior deja de imprimirse.
    const onPasarADosColumnas = () => {
        formSet({...formValues, ...getColumnasEquivalentes(formValues.firmas)})
        setFirmasEnColumnas(true)
        setCampoActivo('firmasIzquierda')
    }

    const onParametroSelected = (codigo) => {
        const { selectionStart, selectionEnd } = document.getElementById(campoActivo)
        const oldValue = formValues[campoActivo]
        const newValue = oldValue.slice(0, selectionStart) + `[[${codigo}]]` + oldValue.slice(selectionEnd)

        const newValues = {...formValues}
        newValues[campoActivo] = newValue
        formSet(newValues)
    }

    const getFirmas = () => firmasEnColumnas
        ? { firmas: '', firmasIzquierda: formValues.firmasIzquierda, firmasDerecha: formValues.firmasDerecha }
        : { firmas: formValues.firmas, firmasIzquierda: '', firmasDerecha: '' }

    const { errorOf, validate } = useFormErrors(getErrors)

    function getErrors() {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED
        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (formValues.convenio.length < 1) errors.convenio = VALIDATION_MESSAGE.REQUIRED
        if (formValues.condiciones.length < 1) errors.condiciones = VALIDATION_MESSAGE.REQUIRED

        return errors
    }

    const onConfirm = () => {
        if (getErrors().condiciones) onTabSelected('condiciones')
        if (!validate()) return

        setIsLoading(true)

        if (props.editMode === 'add') {
            ServerRequest(
                REQUEST_METHOD.POST,
                null,
                true,
                APIS.URLS.TIPO_PLAN_PAGO,
                null,
                {...entity, ...formValues, ...getFirmas()},
                res => res.json().then(data => {
                    setIsLoading(false)
                    props.onConfirm()
                }),
                res => {
                    onRequestNoSuccess(res)
                    setIsLoading(false)
                },
                error => {
                    onRequestError(error)
                    setIsLoading(false)
                },
            )
        }

        else {
            ServerRequest(
                REQUEST_METHOD.PUT,
                null,
                true,
                APIS.URLS.TIPO_PLAN_PAGO,
                `/${entity.id}`,
                {...entity, ...formValues, ...getFirmas()},
                res => res.json().then(data => {
                    setIsLoading(false)
                    props.onConfirm()
                }),
                res => {
                    onRequestNoSuccess(res)
                    setIsLoading(false)
                },
                error => {
                    onRequestError(error)
                    setIsLoading(false)
                },
            )
        }
    }

    const tableColumnsParametros = [
        {
            Header: '',
            Cell: ({ row: { original }}) => (
                <div className='action'>
                    <div onClick={() => onParametroSelected(original.codigo)} className="link">
                    <span className="material-symbols-outlined" title="Seleccionar">west</span>
                    </div>
                </div>
            ),
            id:'select',
            accessor: 'id',
            width: '5%',
            disableGlobalFilter: true,
            disableSortBy: true
        },{
            Header: 'Código',
            accessor: 'codigo',
            width: '95%',
            Cell: ({ row: { original }}) => (
                <span style={{fontSize: 11,}}>
                    <strong className="input-formula-parametro">
                        {original.codigo}
                    </strong>
                    : {original.nombre}
                </span>
            ),
        }
    ]

    const parametroFechaActual = {id: 0, codigo: "FECHA_FIRMA", nombre: "Fecha de firma", orden: 0}

    return (
        <Modal
            onEscape={props.onCancel}
            show
            size='xl'
            tipName={TIPS.NAME.MODALS.CONVENIO_DEFINICIONES_MODAL}
            allowFullscreen
            header={<h2 className="modal-title">Definición de convenio: {(entity.id > 0) ? entity.nombre : "Nuevo"}</h2>}
            body={(
                <div className='row form-basic'>
                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="codigo" required>Código</FormLabel>
                        <input
                            type="text"
                            name="codigo"
                            maxLength={50}
                            className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                            value={formValues.codigo}
                            onChange={formHandle}
                            disabled={props.editMode == 'view'}
                        />
                        <FormError message={errorOf('codigo')} />
                    </div>
                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                        <input
                            type="text"
                            name="nombre"
                            maxLength={250}
                            className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                            value={formValues.nombre}
                            onChange={formHandle}
                            disabled={props.editMode == 'view'}
                        />
                        <FormError message={errorOf('nombre')} />
                    </div>
                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="convenio" required>Convenio</FormLabel>
                        <input
                            type="text"
                            name="convenio"
                            maxLength={250}
                            className={`form-control ${errorOf('convenio') ? 'is-invalid' : ''}`}
                            value={formValues.convenio}
                            onChange={formHandle}
                            disabled={props.editMode == 'view'}
                        />
                        <FormError message={errorOf('convenio')} />
                    </div>
                    <div className="col-12 col-md-6">
                        <label htmlFor="idTipoTRibuto" className="form-label">Tipo de tributo</label>
                        <InputLista
                            name="idTipoTributo"
                            placeholder=""
                            className="form-control"
                            value={formValues.idTipoTributo}
                            onChange={formHandle}
                            disabled={props.editMode == 'view'}
                            lista="TipoTributo"
                            textItemZero="Todos los tributos"
                        />
                        <FormError />
                    </div>
                    <div className="col-12 convenio-definicion-form-separated m-top-20">
                        <div className='col-md-8 convenio-definicion-condiciones'>
                        <label className="form-label">Secciones</label>
                        <Tabs
                            id="tabs-convenio"
                            activeKey={tabActive}
                            onSelect={onTabSelected}
                            >
                            <Tab eventKey={"condiciones"} title="Condiciones">
                                <div className='tab-panel'>
                                    <textarea
                                        id="condiciones"
                                        className={`form-control ${errorOf('condiciones') ? 'is-invalid' : ''}`}
                                        maxLength={12000}
                                        name="condiciones"
                                        value={formValues.condiciones}
                                        onChange={formHandle}
                                        onFocus={() => setCampoActivo('condiciones')}
                                        disabled={props.editMode == 'view'}
                                    />
                                    <FormError message={errorOf('condiciones')} />
                                </div>
                            </Tab>
                            <Tab eventKey={"firmas"} title="Firmas">
                                <div className='tab-panel'>
                                    {firmasEnColumnas ? (
                                        <div className='row convenio-definicion-firmas'>
                                            <div className='col-6'>
                                                <label htmlFor="firmasIzquierda" className="form-label">Columna izquierda</label>
                                                <textarea
                                                    id="firmasIzquierda"
                                                    className="form-control"
                                                    maxLength={4000}
                                                    name="firmasIzquierda"
                                                    value={formValues.firmasIzquierda}
                                                    onChange={formHandle}
                                                    onFocus={() => setCampoActivo('firmasIzquierda')}
                                                    disabled={props.editMode == 'view'}
                                                />
                                            </div>
                                            <div className='col-6'>
                                                <label htmlFor="firmasDerecha" className="form-label">Columna derecha</label>
                                                <textarea
                                                    id="firmasDerecha"
                                                    className="form-control"
                                                    maxLength={4000}
                                                    name="firmasDerecha"
                                                    value={formValues.firmasDerecha}
                                                    onChange={formHandle}
                                                    onFocus={() => setCampoActivo('firmasDerecha')}
                                                    disabled={props.editMode == 'view'}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='convenio-definicion-firmas'>
                                            <textarea
                                                id="firmas"
                                                className="form-control"
                                                maxLength={4000}
                                                name="firmas"
                                                value={formValues.firmas}
                                                onChange={formHandle}
                                                onFocus={() => setCampoActivo('firmas')}
                                                disabled={props.editMode == 'view'}
                                            />
                                            {props.editMode !== 'view' && (
                                                <button type="button" className="btn btn-outline-primary m-top-10" onClick={onPasarADosColumnas}>Pasar a dos columnas</button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Tab>
                        </Tabs>
                        </div>
                        {props.editMode !== 'view' && 
                        <div className='col-12 col-md-4'>
                            <label className="form-label">Parámetros</label>
                            <TableCustom
                                showDownloadCSV={false}
                                showHeader={false}
                                showFilterGlobal={false}
                                showPagination={true}
                                showPageSize={false}
                                pageSize={10}
                                className={'TableCustomBase m-top-40'}
                                columns={tableColumnsParametros}
                                data={[parametroFechaActual, ...props.parametros]}
                                messageEmpty="No se encontraron parámetros"
                            />
                        </div>
                        }
                    </div>
                </div>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={props.onCancel}>Volver</button>
                    {props.editMode !== 'view' && (
                        <button className="btn action-button" data-dismiss="modal" onClick={() => onConfirm()}>Guardar</button>
                    )}
                </div>
                
            )}
        />
    )
}

ConvenioDefinicionesModal.propTypes = {
    convenio: object.isRequired,
    parametros: array.isRequired,
    editMode: string,
    onConfirm: func.isRequired,
    onCancel: func.isRequired,
};

ConvenioDefinicionesModal.defaultProps = {
    editMode: 'view'
};

export default ConvenioDefinicionesModal
